import { useEffect, useState } from 'react';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useFormState } from 'informed';
import api from 'api';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader
} from 'components/common/box';
import { AchievementCompletionsAddTableRow } from '.';

import type { PageState } from 'types';
import type { AchievementCompletionAddFormValues } from './AchievementCompletionsAdd';

interface Props {
  achievement: PageState['achievement'];
  alreadyAddedUsersByEmail: { [key: string]: boolean };
  alreadyAddedUsersByUsername: { [key: string]: boolean };
  setUploader: (id: string, uploader: Function) => void;
  usersWithInProgressAchievementByEmail: { [key: string]: boolean };
  usersWithInProgressAchievementByUsername: { [key: string]: boolean };
}

const AchievementCompletionsAddTable = ({
  achievement,
  alreadyAddedUsersByEmail,
  alreadyAddedUsersByUsername,
  setUploader,
  usersWithInProgressAchievementByEmail,
  usersWithInProgressAchievementByUsername
}: Props) => {
  const formState = useFormState<AchievementCompletionAddFormValues>();

  const { language, messages } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  const [existingUsersByEmail, setExistingUsersByEmail] = useState({});
  const [existingUsersByUsername, setExistingUsersByUsername] = useState({});

  useEffect(() => {
    const getExistingUsers = async () => {
      try {
        const response = await api.post('/admin/users-existing', {
          emails:
            formState.values.emails !== undefined
              ? formState.values.emails || []
              : undefined,
          usernames:
            formState.values.usernames !== undefined
              ? formState.values.usernames || []
              : undefined
        });
        setExistingUsersByEmail(response.data.usersByEmail);
        setExistingUsersByUsername(response.data.usersByUsername);
      } catch (error) {
        setExistingUsersByEmail({});
        setExistingUsersByUsername({});
      }
    };
    getExistingUsers();
  }, [
    JSON.stringify(formState.values.emails),
    JSON.stringify(formState.values.usernames)
  ]);

  return formState.values.emails?.length ||
    formState.values.usernames?.length ? (
    <Box
      padding={0}
      style={{ borderRadius: 0, maxHeight: 400, overflow: 'scroll' }}
    >
      <BoxOverflowWrapper style={{ borderRadius: 0 }} useDarkerHighlight={true}>
        <BoxTable>
          <BoxTableHeader>
            <BoxTableCell shrink={true} />
            <BoxTableCell sticky={true}>{messages.commonUser}</BoxTableCell>
            {hasStartedAt ? (
              <BoxTableCell>{messages.commonStartDate}</BoxTableCell>
            ) : null}
            {hasCompletion ? (
              <BoxTableCell>{messages.completionsCompletionDate}</BoxTableCell>
            ) : null}
            {hasExpiration ? (
              <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>
            ) : null}
            <BoxTableCell>{messages.commonComment}</BoxTableCell>
            <BoxTableCell>{messages.commonAttachments}</BoxTableCell>
            {achievement?.userAttributes?.map((attribute) => (
              <BoxTableCell key={attribute.id}>
                {attribute.titleTranslations?.[language] || attribute.title}
              </BoxTableCell>
            ))}
          </BoxTableHeader>

          {formState.values.emails?.map((email, i) => (
            <AchievementCompletionsAddTableRow
              achievement={achievement}
              completionDate={formState.values.completionDate as string}
              email={email}
              existingUser={existingUsersByEmail[email]}
              expirationDate={formState.values.expirationDate as string}
              id={i}
              isAlreadyAddedUser={Boolean(alreadyAddedUsersByEmail[email])}
              isAlreadyAddedUserInprogress={Boolean(
                usersWithInProgressAchievementByEmail[email]
              )}
              key={email}
              setUploader={setUploader}
            />
          ))}

          {formState.values.usernames?.map((username, i) => (
            <AchievementCompletionsAddTableRow
              achievement={achievement}
              completionDate={formState.values.completionDate as string}
              existingUser={existingUsersByUsername[username]}
              expirationDate={formState.values.expirationDate as string}
              id={i}
              isAlreadyAddedUser={Boolean(
                alreadyAddedUsersByUsername[username]
              )}
              isAlreadyAddedUserInprogress={Boolean(
                usersWithInProgressAchievementByUsername[username]
              )}
              key={username}
              setUploader={setUploader}
              username={username}
            />
          ))}
        </BoxTable>
      </BoxOverflowWrapper>
    </Box>
  ) : null;
};

export default AchievementCompletionsAddTable;
